import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { useCopyToClipboard } from '@components/Button/CopyToClipboard';
import { Alert, Button } from '@eye-able/ui';
import { Copy, Download } from 'lucide-react';

interface BackupCodesViewProps {
  codes: string[];
  /** True right after the first method went live; the codes then open with that news. */
  justEnabled?: boolean;
}

function downloadAsText(codes: string[]) {
  const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'eye-able-backup-codes.txt';
  link.click();
  URL.revokeObjectURL(url);
}

/** The one-time codes, shown once; the API keeps only hashes. */
function BackupCodesView({ codes, justEnabled = false }: BackupCodesViewProps) {
  const { t } = useInlineTranslation();
  const copy = useCopyToClipboard();

  return (
    <div className='eye:flex eye:flex-col eye:gap-16'>
      {justEnabled ? (
        <Alert variant='success'>
          <T k='mfaActivated' />
        </Alert>
      ) : null}
      <p className='eye:text-14 eye:leading-22'>
        <T k='mfaBackupCodesOnceHint' />
      </p>
      <Alert variant='warning'>
        <T k='mfaBackupCodesStoreSafely' />
      </Alert>
      <ol
        aria-label={t('mfaBackupCodes')}
        className='eye:grid eye:grid-cols-2 eye:gap-x-24 eye:gap-y-8 eye:rounded-md eye:bg-grey-10 eye:px-20 eye:py-16 eye:font-mono eye:text-16 eye:font-medium eye:tracking-wide eye:tabular-nums'
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ol>
      <div className='eye:flex eye:gap-8'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='eye:flex-1 eye:md:text-14'
          onClick={() => downloadAsText(codes)}
        >
          <Download aria-hidden />
          {t('mfaDownloadBackupCodes')}
        </Button>
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='eye:flex-1 eye:md:text-14'
          onClick={() => void copy(codes.join('\n'))}
        >
          <Copy aria-hidden />
          {t('copy')}
        </Button>
      </div>
    </div>
  );
}

export default BackupCodesView;
